




















































import { Component, Vue, Prop, Emit } from 'vue-property-decorator';
import dayjs from 'dayjs';
import {
  Representative,
  CustomTile,
  TaskDetailParam,
  AttributeName,
  AttributeKey,
  ColumnSwitch,
  TaskChildViewAttributeGroup,
  TaskAttributeGroup,
} from '@/@types/LibraryComponent';
import TaskListTableHeader from '@/components/taskList/TaskListTableHeader.vue';
import EditableText from '@/components/common/EditableText.vue';
import EditableTextSchedule from '@/components/common/EditableTextSchedule.vue';
import EditableTextPerformance from '@/components/common/EditableTextPerformance.vue';
import AccountIcon from '@/components/common/AccountIcon.vue';
import CommonButton from '@/components/common/CommonButton.vue';
import DatePickerMenu from '@/components/taskList/taskListTableParts/DatePickerMenu.vue';
import PersonMenu from '@/components/taskList/taskListTableParts/PersonMenu.vue';
import SelectMenu from '@/components/taskList/taskListTableParts/SelectMenu.vue';
import AccentSelectMenu from '@/components/taskList/taskListTableParts/AccentSelectMenu.vue';
import { TasksType } from '@/@types/Task';
import {
  SelectTaskList,
  CustomAttribute,
  TaskAttribute,
  DefaultTaskTableAttributes,
  Tile,
} from '@/@types/LibraryComponent';
import { AttributeCd, TaskAttributeValueOption } from '@/@types/Project';
import { vxm } from '@/store';
import { ATTRIBUTE_STYLE } from '@/@types/constant';

@Component({
  components: {
    TaskListTableHeader,
    EditableText,
    EditableTextSchedule,
    EditableTextPerformance,
    AccountIcon,
    CommonButton,
    DatePickerMenu,
    PersonMenu,
    SelectMenu,
    AccentSelectMenu,
  },
})
export default class TaskListTableBody extends Vue {
  @Prop()
  private task!: TasksType;
  /** 担当者、実施者などの一覧 */
  @Prop()
  private readonly representatives!: Representative[];
  /** 課題一覧 */
  @Prop()
  private readonly tasks!: TasksType[];
  @Prop({ required: true })
  private taskChildViewAttributeGroups!: TaskChildViewAttributeGroup[];
  @Prop()
  private priorities!: TaskAttributeGroup[];
  // カテゴリーリスト
  @Prop()
  private categories!: TaskAttributeGroup[];
  // ステータスリスト
  @Prop()
  private statuses!: TaskAttributeGroup[];
  @Prop()
  private customAttributes!: CustomAttribute[];
  @Prop()
  private customTiles!: CustomTile[];
  @Prop()
  private fieldColumns!: ColumnSwitch[];

  private currentProjectName = vxm.theoryApi.projectsStore.getProjectBasic?.projectName ?? '';

  private bindComponent(attribute: TaskAttribute) {
    const style = attribute.style;
    const attributeName = attribute.attribute;
    if (style === ATTRIBUTE_STYLE.DATE) {
      return DatePickerMenu;
    } else if (style === ATTRIBUTE_STYLE.PERSON) {
      return PersonMenu;
    } else if (attributeName === AttributeName.scheduledTime) {
      return EditableTextSchedule;
    } else if (attributeName === AttributeName.completedTime) {
      return EditableTextPerformance;
    } else if (
      style === ATTRIBUTE_STYLE.TEXT ||
      style === ATTRIBUTE_STYLE.NUMBER
    ) {
      return EditableText;
    } else if (attributeName === AttributeName.priority) {
      return AccentSelectMenu;
    } else if (style === ATTRIBUTE_STYLE.OPTION) {
      return SelectMenu;
    } else {
      return EditableText;
    }
  }

  /** デフォルト属性とカスタム属性を合わせてTaskAttribute[]型を作成 */
  private taskTableAttributes() {
    let attributes: TaskAttribute[] = [];
    attributes = attributes.concat(DefaultTaskTableAttributes);
    attributes[0].value = this.task.taskName;
    attributes[1].value = this.task.category;
    attributes[2].value = this.task.currentResponsiblePerson
      ? this.task.currentResponsiblePerson.accountName
      : null;
    attributes[3].value = this.task.priority;
    attributes[4].value = this.task.status;
    attributes[5].value = this.task.practitioner
      ? this.task.practitioner.accountName
      : null;
    attributes[6].value = this.task.startDate;
    attributes[7].value = this.task.deadline;
    attributes[8].value = this.task.scheduledTime;
    attributes[9].value = this.task.completedTime;
    /** カスタム属性を取得 */
    if (this.customAttributes.length > 0) {
      this.customAttributes.forEach((attribute) => {
        /** カスタム属性を識別する数字 */
        const index = attribute.attributeNumber;
        let value: string | number | Date | null = null;
        let attributeKey: string = '';
        /** 属性の形式から、カスタム属性値を取得する */
        switch (attribute.style) {
          /** 日付形式 */
          case ATTRIBUTE_STYLE.DATE:
            attributeKey = `customAttribute${index}ValueDate`;
            value = this.task[attributeKey];
            break;
          /** 人物形式 */
          case ATTRIBUTE_STYLE.PERSON:
            attributeKey = `customAttribute${index}ValuePerson`;
            const person = this.representatives.find((member) => {
              return member.accountId === this.task[attributeKey];
            });
            if (person) {
              value = person.accountName ? person.accountName : '担当者なし';
            }
            break;
          /** テキスト形式 */
          case ATTRIBUTE_STYLE.TEXT:
            attributeKey = `customAttribute${index}ValueText`;
            value = this.task[attributeKey];
            break;
          /** 選択肢形式 */
          case ATTRIBUTE_STYLE.OPTION:
            attributeKey = `customAttribute${index}ValueOption`;
            const taskAttributeValueOption = this.taskAttributeValueOptions.find(
              (option) => {
                return (
                  option.taskAttributeValueOptionId === this.task[attributeKey]
                );
              }
            );
            value = taskAttributeValueOption
              ? taskAttributeValueOption.value
              : null;
            break;
          /** 数値形式 */
          case ATTRIBUTE_STYLE.NUMBER:
            attributeKey = `customAttribute${index}ValueNumber`;
            value = this.task[attributeKey]
              ? this.task[attributeKey].toString()
              : null;
            break;
          default:
            break;
        }
        /** デフォルト属性の配列に結合するためのカスタム属性 */
        const customAttribute: TaskAttribute = {
          attribute: attribute.name,
          attributeKey: attributeKey,
          attributeNumber: attribute.attributeNumber,
          style: attribute.style,
          displayStyle: attribute.displayStyle,
          displayFlag: attribute.displayFlag,
          value: value,
          customFlag: true,
        };
        attributes.push(customAttribute);
      });
    }
    return attributes;
  }

  private getTiles(
    attribute: TaskAttribute
  ): TaskAttributeGroup[] | Tile[] | null {
    if (attribute.attributeKey === AttributeKey.category) {
      return this.categories;
    } else if (attribute.attributeKey === AttributeKey.status) {
      return this.statuses;
    } else if (attribute.attributeKey === AttributeKey.priority) {
      return this.priorities;
    } else {
      const filteredTile = this.customTiles.find((tile) => {
        return tile.attributeNumber === attribute.attributeNumber;
      });
      return filteredTile ? filteredTile.customTiles : null;
    }
  }

  private dateValueToString(value: Date | string | null): string | null {
    if (value instanceof Date) {
      return dayjs(value).format('YYYY/MM/DD');
    } else if (typeof value === 'string') {
      if (new Date(value)) {
        return dayjs(new Date(value)).format('YYYY/MM/DD');
      } else {
        return value;
      }
    } else {
      return value;
    }
  }

  /** 各コンポーネントにbindしたいclassを定義 */
  private bindClass(attribute: TaskAttribute) {
    const style = attribute.style;
    const attributeName = attribute.attribute;
    return {
      'person-menu': style === ATTRIBUTE_STYLE.PERSON,
      time:
        attributeName === AttributeName.scheduledTime ||
        attributeName === AttributeName.completedTime,
    };
  }

  private get selectTaskList(): SelectTaskList[] {
    return this.tasks.map((task) => {
      const selectTask: SelectTaskList = {
        taskId: task.taskId,
        taskName: task.taskName,
      };
      return selectTask;
    });
  }

  private get projectId() {
    return vxm.theoryApi.authStore.getCurrentProjectId;
  }

  private get taskAttributeValueOptions(): TaskAttributeValueOption[] {
    const response = vxm.theoryApi.projectsStore.getTaskAttributeValueOptions;
    if (response) {
      return response.results;
    } else return [];
  }

  @Emit('emitTaskData')
  private emitTaskData(value: TaskDetailParam): TaskDetailParam {
    return value;
  }
  /**
   * タスク詳細を開く
   */
  @Emit('openTaskModal')
  private async openTaskModal(taskId: number) {
    await vxm.theoryApi.tasksStore.fetchTask({
      taskId: taskId,
      query: {
        'project-id': this.projectId,
      },
    });
    return;
  }
}
